import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import {
  IAbstractProduct,
  IAbstractProductWithIncluded,
} from '../models/abstract-product.models';
import { catchError } from 'rxjs/operators';
import { GlueConfigurationService } from './glue-configuration.service';
import { GlueUtils } from '../utils/glue.utils';
import { EGlueResource } from '../configurations/common';
import { ICategoryTreeNode } from '../models/catalog.models';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly glueConfiguration: GlueConfigurationService = inject(GlueConfigurationService);

  baseUrl: string = this.glueConfiguration.getEndpointUrl();

  private readonly httpParamsForProductsCategoryTree: { params: HttpParams } = {
    params: new HttpParams()
      .set('include', 'category-nodes'),
  };

  private readonly httpParamsForAbstractProduct: { params: HttpParams } = {
    params: new HttpParams()
      .set('include', 'install-base,abstract-product-image-sets,abstract-product-prices,product-labels,concrete-products,concrete-product-prices'),
  };

  private static handleError(error: any): Promise<any> {
    console.error('An error occurred ', error);
    return Promise.reject(error.message || error);
  }

  getAbstractProductDataFromApi(sku: string): Observable<IAbstractProductWithIncluded> {
    return this.http.get<IAbstractProductWithIncluded>(this.baseUrl + '/abstract-products/' + sku,
      this.httpParamsForAbstractProduct).pipe(catchError(this.handleError));
  }

  getProductBySku(sku: string): Observable<IAbstractProduct> {
    return this.http.get<IAbstractProduct>(this.baseUrl + '/abstract-products/' + sku)
      .pipe(catchError(this.handleError));
  }

  getProductWithCategoryTree(id: string): Promise<ICategoryTreeNode> {
    return this.http.get<IAbstractProduct>(this.baseUrl + '/abstract-products/' + id,
      this.httpParamsForProductsCategoryTree)
      .toPromise()
      .then((response) => {
        return response;
      })
      .catch(ProductsService.handleError);
  }

  getAbstractProductPricesAndAvailabilitiesFromSap(sku: string, quoteUuid: string): Observable<any> {
    const params: HttpParams = GlueUtils.addIncludeParam(
      new HttpParams(),
      [EGlueResource.ABSTRACT_PRODUCT_PRICES, EGlueResource.ABSTRACT_PRODUCT_AVAILABILITIES],
    ).set('quote-uuid', quoteUuid);
    return this.http.get(this.baseUrl + '/abstract-products/' + sku, {params})
      .pipe(catchError(this.handleError));
  }

  handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(error);
  }
}
